import axios from 'axios';
import { useEffect, useState, Fragment } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import Cookies from 'js-cookie';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Heart,
  Trash2,
  Calendar,
  Search,
  Home,
  X,
  AlertCircle,
  Clock,
  LayoutGrid
} from 'lucide-react';
import FormatDateTime from '../../shared/utils/dateUtils';
import FormatPrice from '../properties/components/shared/PriceFormatter';

export default function CartSystem() {
  const session_id = Cookies.get('jwt');
  const [dataCarts, setDataCarts] = useState([]);
  const [tempDisplayRemoveCartPost, setTempDisplayRemoveCartPost] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    if (session_id === undefined) {
      window.location.replace('/login');
    } else {
      getDataCarts(1, '', '');
    }
    const savedPosition = Cookies.get('scrollPosition');
    if (savedPosition) window.scrollTo(0, parseInt(savedPosition, 10));
  }, []);

  useEffect(() => {
    const handleScroll = () => Cookies.set('scrollPosition', window.pageYOffset);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleRemoveAsset = (postId) => {
    Swal.fire({
      title: 'ลบทรัพย์นี้ออกใช่หรือไม่?',
      text: 'คุณสามารถกลับมาค้นหาและเพิ่มใหม่ได้เสมอ',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#34bfd9',
      cancelButtonColor: '#1e293b',
      confirmButtonText: 'ยืนยันการลบ',
      cancelButtonText: 'ยกเลิก',
      customClass: { popup: 'rounded-[2rem] font-["Prompt"]', confirmButton: 'rounded-xl font-bold', cancelButton: 'rounded-xl font-bold' },
    }).then((result) => {
      if (result.isConfirmed) {
        axios.delete(`${process.env.REACT_APP_API}/event/removeBasket`, {
          data: { assetId: postId },
          headers: { Authorization: `Bearer ${Cookies.get('jwt')}` },
        })
          .then(async () => {
            setTempDisplayRemoveCartPost(postId);
            setPage(1);
            getDataCarts(1, startDate, endDate);
            await Swal.fire({ title: 'ลบเรียบร้อย!', icon: 'success', confirmButtonColor: '#34bfd9', customClass: { popup: 'rounded-[2rem] font-["Prompt"]' } });
          })
          .catch((err) => Swal.fire('ไม่สำเร็จ!', `${err.response?.data?.Error || 'เกิดข้อผิดพลาด'}`, 'error'));
      }
    });
  };

  const getDataCarts = async (pg, sd, ed) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API}/event/getBasket`, {
        headers: { Authorization: `Bearer ${Cookies.get('jwt')}` },
        params: { page: pg, startDate: sd, endDate: ed },
      });
      if (pg === 1) {
        setDataCarts(response.data.assets);
      } else {
        setDataCarts((prev) => [...prev, ...response.data.assets]);
      }
      setHasMore(response.data.assets.length > 0);
    } catch (error) {
      console.error('Error fetching basket data:', error);
    }
  };

  const handleLoadMore = () => {
    if (hasMore) {
      const newPage = page + 1;
      setPage(newPage);
      getDataCarts(newPage, startDate, endDate);
    }
  };

  const handleSearch = () => {
    setPage(1);
    getDataCarts(1, startDate, endDate);
  };

  const clearFilters = () => {
    setStartDate('');
    setEndDate('');
    setPage(1);
    getDataCarts(1, '', '');
  };

  const hasActiveFilter = startDate || endDate;

  return (
    <div className="min-h-screen bg-gray-50">

      {/* ── Header ── */}
      <div className="bg-white border-b border-gray-100 px-4 py-6 lg:px-8">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">My Favorites</p>
            <h1 className="text-2xl font-bold text-gray-900">ทรัพย์ที่ถูกใจ</h1>
          </div>
          <div className="flex items-center gap-2 bg-red-50 px-4 py-2 rounded-full">
            <Heart className="w-5 h-5 text-red-400 fill-red-400" />
            <span className="text-base font-semibold text-red-500">{dataCarts.length} รายการ</span>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 lg:px-8 space-y-5">

        {/* ── Filter Section ── */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">กรองรายการ</p>

          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-400 font-medium">วันเริ่มต้น</label>
              <div className="flex items-center gap-2.5 bg-gray-50 border border-gray-200 rounded-xl px-4 focus-within:border-blue-400 transition-all">
                <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="bg-transparent w-full text-sm text-gray-700 outline-none py-3.5"
                />
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-400 font-medium">วันสิ้นสุด</label>
              <div className="flex items-center gap-2.5 bg-gray-50 border border-gray-200 rounded-xl px-4 focus-within:border-blue-400 transition-all">
                <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="bg-transparent w-full text-sm text-gray-700 outline-none py-3.5"
                />
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            {hasActiveFilter && (
              <button
                onClick={clearFilters}
                className="h-12 px-4 bg-red-50 border-2 border-red-100 text-red-400 rounded-xl flex items-center gap-2 font-semibold text-sm hover:bg-red-100 transition-colors"
              >
                <X className="w-4 h-4" />
                ล้าง
              </button>
            )}
            <button
              onClick={handleSearch}
              className="flex-1 h-12 bg-blue-500 text-white rounded-xl text-sm font-bold flex items-center justify-center gap-2 hover:bg-blue-600 transition-colors shadow-sm"
            >
              <Search className="w-4 h-4" />
              ค้นหา
            </button>
          </div>
        </div>

        {/* ── Property Grid ── */}
        {dataCarts.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <AnimatePresence mode="popLayout">
              {dataCarts.map((post) => (
                <motion.div
                  key={post.id}
                  layout
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.25 }}
                  className={`bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden group ${tempDisplayRemoveCartPost === post.id ? 'hidden' : ''} ${post.sold || post.ban ? 'opacity-60' : ''}`}
                >
                  <div className="relative w-full h-52 overflow-hidden">
                    <Link to={post.status === false ? `/product/${post.id}` : '/home'}>
                      <img
                        src={post.images_url.AssetImagesURL[0]}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        alt={post.title}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                    </Link>

                    {(post.sold || post.ban) && (
                      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-20">
                        <div className="bg-white/10 border border-white/20 px-4 py-3 rounded-xl text-center">
                          <AlertCircle className="w-6 h-6 text-white mx-auto mb-1" />
                          <p className="text-white text-xs font-semibold">ทรัพย์นี้ถูกลบหรือขายแล้ว</p>
                        </div>
                      </div>
                    )}

                    <span className="absolute top-3 left-3 bg-white/90 text-gray-700 text-xs font-semibold px-2.5 py-1 rounded-full shadow-sm">
                      #{post.id}
                    </span>

                    <button
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleRemoveAsset(post.id); }}
                      className="absolute top-3 right-3 w-9 h-9 bg-white/90 rounded-full flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors shadow-sm z-30"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="px-4 pt-4 pb-5">
                    <p className="text-lg font-bold text-emerald-600 mb-1">
                      <FormatPrice number={post.sell_price == null ? '' : post.sell_price} suffix={`${post.sell_price == null ? '' : ' บาท'}`} />
                    </p>
                    <p className="text-sm font-semibold text-gray-900 leading-snug line-clamp-2">{post.title}</p>
                    <p className="text-xs text-gray-400 mt-1 line-clamp-1">{post.description}</p>

                    <div className="flex flex-wrap gap-2 mt-3">
                      {post.rooms.Bedroom > 0 && (
                        <span className="flex items-center gap-1 text-xs bg-gray-50 border border-gray-100 rounded-full px-2.5 py-1 text-gray-600">
                          <Home className="w-3 h-3" /> {post.rooms.Bedroom} ห้องนอน
                        </span>
                      )}
                      {post.rooms.Bathroom > 0 && (
                        <span className="flex items-center gap-1 text-xs bg-gray-50 border border-gray-100 rounded-full px-2.5 py-1 text-gray-600">
                          <LayoutGrid className="w-3 h-3" /> {post.rooms.Bathroom} ห้องน้ำ
                        </span>
                      )}
                      {post.type && (
                        <span className="text-xs bg-blue-50 border border-blue-100 text-blue-600 rounded-full px-2.5 py-1 font-semibold">
                          {post.type}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-gray-100">
                      <Clock className="w-3.5 h-3.5 text-gray-300" />
                      <span className="text-xs text-gray-400"><FormatDateTime isoDateString={post.date} /></span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        ) : (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white rounded-2xl shadow-sm border border-gray-100 py-24 text-center">
            <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-5">
              <Heart className="w-10 h-10 text-gray-200" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">ยังไม่มีทรัพย์ที่ถูกใจ</h3>
            <p className="text-sm text-gray-400 mb-8">เริ่มต้นค้นหาและบันทึกทรัพย์ที่คุณสนใจ</p>
            <Link to="/home">
              <button className="bg-blue-500 text-white px-8 py-3 rounded-full text-base font-semibold hover:bg-blue-600 transition-colors">
                ไปสำรวจทรัพย์สิน
              </button>
            </Link>
          </motion.div>
        )}

        {hasMore && dataCarts.length > 0 && (
          <div className="text-center pt-2">
            <button
              onClick={handleLoadMore}
              className="bg-white border border-gray-200 text-gray-600 px-10 py-3 rounded-full text-base font-semibold hover:bg-gray-50 transition-colors shadow-sm"
            >
              โหลดเพิ่มเติม
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
